import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../context/authContext";
import {
  Paginated,
  PostSettingsBody,
  Settings,
  Setup,
  cloneSetup,
  deleteSetup,
  exportSetup,
  getSettings,
  getSetup,
  listSetups,
  postSettings,
  postSetup,
} from "../http/onlyclicksApi";
import { usePreferences } from "./useAccount";

export const useSetups = (bikeId?: string, q?: string) => {
  const auth = useAuth();
  return useInfiniteQuery({
    queryKey: ["setups", bikeId, q],
    queryFn: (ctx) => listSetups(ctx.signal, bikeId!, q, ctx.pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_page_token,
    getPreviousPageParam: (firstPage) => firstPage.next_page_token,
    enabled: auth.hasActiveSession && !!bikeId,
    placeholderData: keepPreviousData, // Preserve data between queryKey changes
  });
};

export const useSetups2 = (bikeId?: string, q?: string) => {
  const auth = useAuth();
  return useInfiniteQuery({
    queryKey: ["setups", bikeId, q],
    queryFn: (ctx) => listSetups(ctx.signal, bikeId!, q, ctx.pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_page_token,
    getPreviousPageParam: (firstPage) => firstPage.next_page_token,
    enabled: auth.hasActiveSession && !!bikeId,
    placeholderData: keepPreviousData, // Preserve data between queryKey changes
    select: (data) =>
      data?.pages
        .flatMap((p) => p.items)
        .sort(byCreatedAtDescending)
        .reduce((acc: Record<number, Setup[]>, setup) => {
          const year = new Date(setup.created_at).getFullYear();
          acc[year] = acc[year] ?? [];
          acc[year].push(setup);
          return acc;
        }, {}),
  });
};

const byCreatedAtDescending = (a: Setup, b: Setup): number =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

export const useSetupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      newSetup: Omit<Setup, "id" | "created_at" | "updated_at"> & {
        bike_id: string;
        settings: PostSettingsBody;
      },
    ) => postSetup(newSetup),
    onSuccess: (setup) => {
      queryClient.setQueryData(["setup", setup.id], setup);
      queryClient.invalidateQueries({ queryKey: ["setups"] });
    },
  });
};

export const useDeleteSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSetup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["setups"] });
    },
  });
};

export const useSetup = (setupId: string) => {
  const auth = useAuth();
  return useQuery({
    queryKey: ["setup", setupId],
    queryFn: (ctx) => getSetup(ctx.signal, setupId),
    enabled: auth.hasActiveSession,
  });
};

export const useLatestSettings = (setupId: string) => {
  const auth = useAuth();
  const { data: preferences } = usePreferences();
  return useQuery({
    queryKey: ["settings", setupId],
    queryFn: (ctx) =>
      getSettings(ctx.signal, setupId, preferences!.tire_pressure_unit, 1),
    enabled: auth.hasActiveSession && preferences !== undefined,
    select: (data) => data?.items?.[0],
  });
};

export const useSettingsMutation = (setupId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newSettings: PostSettingsBody) =>
      postSettings(setupId, newSettings),
    onSuccess: (settings) => {
      const cached = queryClient.getQueryData([
        "settings",
        setupId,
      ]) as Paginated<Settings>;
      const updated = {
        ...cached,
        // TODO: pagination limit
        items: [settings, ...cached.items.slice(0, 100 - 1)],
      };
      queryClient.setQueryData(["settings", setupId], updated);
    },
  });
};

export const useExportSetupAsPdf = (setupId: string) => {
  return useMutation({
    mutationFn: () => exportSetup(setupId),
    onSuccess: (pdf) => {
      openPdfInBrowser(pdf);
    },
    onError: (error) => {
      console.error("PDF export failed:", error);
    },
  });
};

const openPdfInBrowser = (pdf: Blob) => {
  const url = window.URL.createObjectURL(pdf);

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = "document.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const useInfiniteSettings = (setupId: string) => {
  const auth = useAuth();
  const { data: preferences } = usePreferences();
  return useInfiniteQuery({
    queryKey: ["settings-todo", setupId], // TODO: Does not work in combination with "settings"
    queryFn: (ctx) =>
      getSettings(
        ctx.signal,
        setupId,
        preferences!.tire_pressure_unit,
        15,
        ctx.pageParam,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_page_token,
    getPreviousPageParam: (firstPage) => firstPage.next_page_token,
    enabled: auth.hasActiveSession && preferences !== undefined,
    placeholderData: keepPreviousData, // Preserve data between queryKey changes
    select: (data) => data?.pages.flatMap((p) => p.items),
  });
};

export const useCloneSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newSetup: Omit<Setup, "created_at" | "updated_at">) =>
      cloneSetup(newSetup.id, newSetup),
    onSuccess: (setup) => {
      queryClient.setQueryData(["setup", setup.id], setup);
      queryClient.invalidateQueries({ queryKey: ["setups"] });
    },
  });
};
